import { useStore } from 'vuex'
import { computed } from 'vue'

// Translation utility function
export function useTranslation() {
  const store = useStore()
  
  const t = (key) => {
    // Handle edge cases
    if (!key || typeof key !== 'string') {
      console.warn('Invalid translation key:', key)
      return key || ''
    }
    
    // Trim the key to remove any leading/trailing spaces
    const trimmedKey = key.trim()
    
    // Check if store is available and language module is initialized
    if (!store || !store.getters || !store.getters['language/t']) {
      console.warn('Language store not available, using key as fallback:', trimmedKey)
      return trimmedKey
    }
    
    // // Debug logging in development
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🔤 Translation called for key:', trimmedKey)
    //   console.log('🔤 Current language:', store.getters['language/currentLanguage'])
    //   console.log('🔤 Available translations:', Object.keys(store.getters['language/translations']))
    // }
    
    try {
      const result = store.getters['language/t'](trimmedKey)
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔤 Translation result:', result)
      }
      
      return result || trimmedKey
    } catch (error) {
      console.error('❌ Translation error for key:', trimmedKey, error)
      return trimmedKey
    }
  }
  
  const currentLanguage = computed(() => {
    try {
      return store.getters['language/currentLanguage'] || 'en'
    } catch (error) {
      console.warn('Language store not available, using default language')
      return 'en'
    }
  })
  
  const availableLanguages = computed(() => {
    try {
      return store.getters['language/availableLanguages'] || []
    } catch (error) {
      console.warn('Language store not available, using empty languages array')
      return []
    }
  })
  
  const loading = computed(() => {
    try {
      return store.getters['language/loading'] || false
    } catch (error) {
      console.warn('Language store not available, using loading false')
      return false
    }
  })
  
  const setLanguage = (language) => {
    try {
      return store.dispatch('language/setLanguage', language)
    } catch (error) {
      console.error('Failed to set language:', error)
      return Promise.reject(error)
    }
  }
  
  const loadTranslations = (platform) => {
    try {
      return store.dispatch('language/loadTranslations', platform)
    } catch (error) {
      console.error('Failed to load translations:', error)
      return Promise.reject(error)
    }
  }
  
  return {
    t,
    currentLanguage,
    availableLanguages,
    loading,
    setLanguage,
    loadTranslations
  }
}

// Global translation function for use outside of components
export function translate(key) {
  const store = window.$store || null
  if (!store) {
    console.warn('Store not available for translation')
    return key
  }
  
  return store.getters['language/t'](key)
}

// Translation keys for common UI elements
export const TRANSLATION_KEYS = {
  // Common actions
  SAVE: ' Save',
  CANCEL: ' Cancel',
  DELETE: ' Delete',
  EDIT: ' Edit',
  ADD: ' Add',
  SUBMIT: ' Submit',
  CONFIRM: ' Confirm',
  BACK: ' Back',
  NEXT: ' Next',
  PREVIOUS: ' Previous',
  CLOSE: ' Close',
  OPEN: ' Open',
  SEARCH: ' Search',
  FILTER: ' Filter',
  SORT: ' Sort',
  REFRESH: ' Refresh',
  EXPORT: ' Export',
  IMPORT: ' Import',
  DOWNLOAD: ' Download',
  UPLOAD: ' Upload',
  
  // Status
  PENDING: ' Pending',
  APPROVED: ' Approved',
  REJECTED: ' Rejected',
  CANCELLED: ' Cancelled',
  COMPLETED: ' Completed',
  IN_PROGRESS: ' In Progress',
  
  // Navigation
  DASHBOARD: ' Dashboard',
  LEAVE: ' Leave',
  ATTENDANCE: ' Attendance',
  TIMESHEET: ' Timesheet',
  EMPLOYEES: ' Employees',
  NOTIFICATIONS: ' Notifications',
  SETTINGS: ' Settings',
  PROFILE: ' Profile',
  LOGOUT: ' Logout',
  LOGIN: ' Login',
  
  // Leave management
  LEAVE_REQUEST: ' Leave Request',
  LEAVE_HISTORY: ' Leave History',
  LEAVE_BALANCE: ' Leave Balance',
  LEAVE_TYPE: ' Leave Type',
  LEAVE_DATES: ' Leave Dates',
  LEAVE_DURATION: ' Leave Duration',
  LEAVE_REASON: ' Leave Reason',
  APPROVE_LEAVE: ' Approve Leave',
  REJECT_LEAVE: ' Reject Leave',
  PENDING_APPROVALS: ' Pending Approvals',
  
  // Attendance
  CHECK_IN: ' Check In',
  CHECK_OUT: ' Check Out',
  ATTENDANCE_HISTORY: ' Attendance History',
  WORK_HOURS: ' Work Hours',
  OVERTIME: ' Overtime',
  
  // Messages
  SUCCESS: ' Success',
  ERROR: ' Error',
  WARNING: ' Warning',
  INFO: ' Information',
  CONFIRMATION: ' Confirmation',
  ARE_YOU_SURE: ' Are you sure?',
  OPERATION_SUCCESSFUL: ' Operation successful',
  OPERATION_FAILED: ' Operation failed',
  DATA_LOADED: ' Data loaded successfully',
  DATA_SAVED: ' Data saved successfully',
  DATA_DELETED: ' Data deleted successfully',
  
  // Form labels
  NAME: ' Name',
  EMAIL: ' Email',
  PHONE: ' Phone',
  ADDRESS: ' Address',
  DEPARTMENT: ' Department',
  POSITION: ' Position',
  START_DATE: ' Start Date',
  END_DATE: ' End Date',
  DESCRIPTION: ' Description',
  COMMENTS: ' Comments',
  ATTACHMENTS: ' Attachments',
  
  // Time
  TODAY: ' Today',
  YESTERDAY: ' Yesterday',
  THIS_WEEK: ' This Week',
  THIS_MONTH: ' This Month',
  THIS_YEAR: ' This Year',
  FROM: ' From',
  TO: ' To',
  DATE: ' Date',
  TIME: ' Time',
  DURATION: ' Duration',
  
  // Numbers
  DAYS: ' Days',
  HOURS: ' Hours',
  MINUTES: ' Minutes',
  SECONDS: ' Seconds',
  TOTAL: ' Total',
  COUNT: ' Count',
  PERCENTAGE: ' Percentage',
  
  // Common phrases
  NO_DATA_FOUND: ' No data found',
  LOADING: ' Loading...',
  PLEASE_WAIT: ' Please wait...',
  SELECT_AN_OPTION: ' Select an option',
  ENTER_TEXT: ' Enter text',
  REQUIRED_FIELD: ' This field is required',
  INVALID_FORMAT: ' Invalid format',
  NETWORK_ERROR: ' Network error',
  SERVER_ERROR: ' Server error',
  UNAUTHORIZED: ' Unauthorized',
  FORBIDDEN: ' Forbidden',
  NOT_FOUND: ' Not found'
} 